import { useState, useEffect } from "react";
import { Plus, Shield, UserCheck, Trash2 } from "lucide-react";
import { adminService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmationModal } from "@/components/shared/ConfirmationModal";
import toast from "react-hot-toast";
import type { Admin, AdminRole } from "@/types";

export function AdminsAdminPage() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: "", displayName: "", role: "editor" as AdminRole });
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);

  useEffect(() => {
    adminService.getAll().then(setAdmins);
  }, []);

  const handleCreate = async () => {
    try {
      await adminService.create({
        uid: "",
        email: newAdmin.email,
        displayName: newAdmin.displayName,
        role: newAdmin.role,
      });
      toast.success("Administrateur ajouté");
      setIsCreating(false);
      setNewAdmin({ email: "", displayName: "", role: "editor" });
      adminService.getAll().then(setAdmins);
    } catch {
      toast.error("Erreur lors de la création");
    }
  };

  const handleDelete = (admin: Admin) => {
    setAdminToDelete(admin);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      await adminService.delete(adminToDelete.id);
      toast.success("Administrateur supprimé");
      setAdmins((prev) => prev.filter((a) => a.id !== adminToDelete.id));
      setAdminToDelete(null);
    } catch {
      toast.error("Erreur");
    }
  };

  const roleConfig = {
    super_admin: { label: "Super Admin", variant: "danger" as const },
    editor: { label: "Éditeur", variant: "warning" as const },
    support: { label: "Support", variant: "success" as const },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion des Admins</h1>
          <p className="text-gray-500 text-sm">{admins.length} administrateurs</p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      {isCreating && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-lg">Nouvel administrateur</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Input
                label="Email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              />
              <Input
                label="Nom"
                value={newAdmin.displayName}
                onChange={(e) => setNewAdmin({ ...newAdmin, displayName: e.target.value })}
              />
              <Select
                label="Rôle"
                value={newAdmin.role}
                onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value as AdminRole })}
                options={[
                  { value: "super_admin", label: "Super Admin" },
                  { value: "editor", label: "Éditeur" },
                  { value: "support", label: "Support" },
                ]}
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleCreate}>Créer</Button>
              <Button variant="outline" onClick={() => setIsCreating(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell className="font-medium">{admin.displayName || "Moi"}</TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Badge variant={roleConfig[admin.role]?.variant || "default"}>
                      {roleConfig[admin.role]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => handleDelete(admin)}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={!!adminToDelete}
        onClose={() => setAdminToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer l'administrateur"
        message={`Êtes-vous sûr de vouloir supprimer ${adminToDelete?.displayName || "cet administrateur"} ?`}
      />
    </div>
  );
}
